/**
 * AmbientesV2 — secao "Ambientes" da pagina do orcamento (Orcamento v2).
 *
 * Fiel ao mockup "Orcamento": card por ambiente (.amb) com tabela de
 * móveis (.móvel) — colunas COD/MOVEL/PUXADOR/MECANISMO/DIMENSOES/QNT/VALOR.
 * As DIMENSOES sao editaveis DIRETO NA LINHA (mascara L × A × P em metros,
 * como no mockup); a linha expandida traz acabamentos + observacoes.
 *
 * Persistencia: save-on-blur (cada campo dispara PATCH quando muda).
 * Formatacao pt-BR: QNT sem zeros a direita ("1", "2,5"); VALOR "1.200,00".
 * Itens CAD: valor read-only (cascata ADR-0019) + dimensoes da composicao +
 * detalhe mostra a composicao (chapas/bordas/ferragens).
 *
 * CSS: classes .orc2 (styles/canonical/orcamento-v2.css).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiChevronDown,
  FiCopy,
  FiLock,
  FiPlus,
  FiTrash2,
  FiUploadCloud,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { listarCotacoes } from "../../../services/cotacoes";
import { listarProdutos } from "../../../services/estoque";
import {
  atualizarAmbiente,
  atualizarItem,
  criarAmbienteNoOrcamento,
  criarItem,
  deletarAmbiente,
  deletarItem,
  recalcularPrecosOrcamento,
} from "../../../services/orcamentos";
import {
  getPrecoOrcamento,
  listarAreasAmbiente,
  listarInsumosAmbiente,
} from "../../../services/motorPreco";
import { fmtMoeda } from "../helpers";
import { ComposicaoCad } from "../AmbienteDetalhes";
// Motor DIVISOR (ADR-0050, UX 2026-07-31): materiais/ferragens POR MOVEL +
// terceiros no card do ambiente.
import AreasM2Editor from "../motor/AreasM2Editor";
import InsumosEditor from "../motor/InsumosEditor";
import CotacoesBloco from "../motor/CotacoesBloco";

// Recarga silenciosa da composicao de preco (padrao do orc2): o
// ComposicaoPrecoDivisor e o proprio AmbientesV2 escutam este evento e
// refazem o GET /preco sem o usuario precisar dar F5.
function invalidarPreco() {
  window.dispatchEvent(new CustomEvent("orc2:preco-invalidado"));
}

function normalizarLista(payload) {
  if (Array.isArray(payload)) return payload;
  return payload?.results || [];
}

function isCad(item) {
  return item?.precificacao_origem === "CAD";
}

function ambienteTemCad(ambiente) {
  return (ambiente?.itens || []).some(isCad);
}

// Nivel m² (ADR-0050): itens gerados por gerar_bom_de_areas carregam
// metadata.fonte = "area_m2" — sao CAD (BOM de material), mas nao vieram do 3D.
function ambienteEhM2(ambiente) {
  return (ambiente?.itens || []).some(
    (i) => i?.metadata?.fonte === "area_m2",
  );
}

// Projecao read-only da cotacao de terceiro (Item TERCEIRO): o ambiente que so
// existe para o cliente ler "Laqueacao" na proposta nao aceita areas/insumos.
function ambienteEhProjecaoTerceiro(ambiente) {
  const itens = ambiente?.itens || [];
  return itens.length > 0
    && itens.every((i) => i?.precificacao_origem === "TERCEIRO");
}

// ---------- formatacao pt-BR dos campos numericos da linha ----------

// "1.000" do backend -> "1" na UI; "2.500" -> "2,5"
function fmtQnt(q) {
  const n = Number(q || 0);
  if (Number.isNaN(n)) return "1";
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

// "1200.00" do backend -> "1.200,00" na UI
function fmtValor(v) {
  const n = Number(v || 0);
  if (Number.isNaN(n)) return "0,00";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Texto digitado ("1.500,50" / "1500,5" / "1500.5" / "2") -> Number | null.
// Com virgula: pontos sao milhar. Sem virgula: ponto unico eh decimal.
function parseDecimal(texto) {
  let t = String(texto || "").trim();
  if (!t) return null;
  if (t.includes(",")) t = t.replace(/\./g, "").replace(",", ".");
  const n = Number(t);
  return Number.isNaN(n) ? null : n;
}

// mm (backend) -> metros com virgula (UI, padrao do mockup "0,80")
function mmParaM(mm) {
  if (mm === null || mm === undefined || mm === "") return "";
  const m = Number(mm) / 1000;
  if (Number.isNaN(m)) return "";
  return m.toFixed(2).replace(".", ",");
}

// metros digitados ("0,8" / "0.80") -> mm para o backend; "" -> null
function mParaMm(texto) {
  const n = parseDecimal(texto);
  if (n === null) return null;
  return (n * 1000).toFixed(2);
}

function dimsCad(item) {
  const d = item?.metadata?.composicao?.modulo_dimensoes_mm;
  if (!d) return null;
  return `${mmParaM(d.w)} × ${mmParaM(d.h)} × ${mmParaM(d.t)}`;
}

// ---------------------------------------------------------------
// Linha de movel (row + detalhe expandido)
// ---------------------------------------------------------------

function MovelRow({
  item,
  codigo,
  editavel,
  onPatch,
  onDelete,
  // Motor DIVISOR (UX 2026-07-31): linhas de material/ferragem DESTE movel
  // (`materiais` = {areas, insumos} ja filtradas) + preco projetado do payload
  // GET /preco ("moveis"). `materiais` null = motor antigo (nada muda).
  ambienteId = null,
  materiais = null,
  produtosChapa = [],
  produtosInsumo = [],
  precoMovel = null,
  onMutateMateriais = null,
  // Terceiros/servicos DESTE movel (ja filtrados pelo card do ambiente).
  orcamento = null,
  ambienteResumo = null,
  cotacoes = [],
  divisorPreco = 0,
  onMutateCotacoes = null,
}) {
  const [aberto, setAberto] = useState(false);
  const [v, setV] = useState(() => ({
    descricao: item.descricao || "",
    puxador: item.puxador || "",
    mecanismo: item.mecanismo || "",
    quantidade: fmtQnt(item.quantidade),
    valor_unitario: fmtValor(item.valor_unitario),
    largura_m: mmParaM(item.largura_mm),
    altura_m: mmParaM(item.altura_mm),
    profundidade_m: mmParaM(item.profundidade_mm),
    material: item.material || "",
    acabamento: item.acabamento || "",
    observacao: item.observacao || "",
  }));

  const cad = isCad(item);
  // Movel com linhas de material/ferragem/terceiro: o preco deixa de ser livre
  // e passa a ser o calculado (preco_projetado do payload /preco). Remover a
  // ultima linha de QUALQUER tipo devolve o campo livre (o backend restaura o
  // valor_unitario digitado) — a cotacao conta igual, porque tambem entra no
  // custo do movel e no numerador.
  const temLinhas = Boolean(
    materiais
    && ((materiais.areas || []).length > 0
      || (materiais.insumos || []).length > 0
      || (cotacoes || []).length > 0),
  );
  const valorPayload = precoMovel
    ? Number(precoMovel.preco_alocado ?? precoMovel.preco_projetado ?? NaN)
    : NaN;
  const valorBloqueado = Number.isFinite(valorPayload)
    ? valorPayload
    : Number(item.quantidade || 0) * Number(item.valor_unitario || 0);
  // Custo lado a lado com o preco (decisao da dona 2026-07-31): o vendedor
  // lanca R$ 450 de chapa e precisa reconhecer os R$ 450 na linha, sem perder
  // a coerencia da soma (o que soma no ambiente e no total e o PRECO).
  const custoMovel = (() => {
    const c = Number(precoMovel?.custo_calculado ?? item.custo_calculado ?? NaN);
    return Number.isFinite(c) && c > 0 ? c : null;
  })();
  const tituloValor = custoMovel !== null
    ? `Custo dos materiais: ${fmtMoeda(custoMovel)}. Preço ao cliente: `
      + `${fmtMoeda(valorBloqueado)} — o custo carrega a cobertura da casa (k) `
      + `e é dividido por (1 − s − L). Remova as linhas para digitar um preço livre.`
    : "Preço calculado dos materiais — remova as linhas para digitar livre";
  const set = (campo) => (e) => setV((p) => ({ ...p, [campo]: e.target.value }));

  // Save-on-blur (texto livre): envia como digitado.
  const salvar = (campo) => () => onPatch({ [campo]: v[campo] });
  // Numericos: parse pt-BR -> formato do backend + re-formata a UI.
  const salvarQnt = () => {
    const n = parseDecimal(v.quantidade) ?? 1;
    setV((p) => ({ ...p, quantidade: fmtQnt(n) }));
    onPatch({ quantidade: String(n) });
  };
  const salvarValor = () => {
    const n = parseDecimal(v.valor_unitario) ?? 0;
    setV((p) => ({ ...p, valor_unitario: fmtValor(n) }));
    onPatch({ valor_unitario: n.toFixed(2) });
  };
  const salvarDims = () => {
    onPatch({
      largura_mm: mParaMm(v.largura_m),
      altura_mm: mParaMm(v.altura_m),
      profundidade_mm: mParaMm(v.profundidade_m),
    });
  };

  // Placeholders L/A/P + tooltip dizem ao usuario qual campo eh qual medida.
  const dimInputs = (
    <span className="dim-mask">
      <input
        className="dim-part"
        value={v.largura_m}
        onChange={set("largura_m")}
        onBlur={salvarDims}
        disabled={!editavel}
        inputMode="decimal"
        placeholder="L"
        title="Largura (m)"
        aria-label="Largura (m)"
      />
      <span className="dim-x">×</span>
      <input
        className="dim-part"
        value={v.altura_m}
        onChange={set("altura_m")}
        onBlur={salvarDims}
        disabled={!editavel}
        inputMode="decimal"
        placeholder="A"
        title="Altura (m)"
        aria-label="Altura (m)"
      />
      <span className="dim-x">×</span>
      <input
        className="dim-part"
        value={v.profundidade_m}
        onChange={set("profundidade_m")}
        onBlur={salvarDims}
        disabled={!editavel}
        inputMode="decimal"
        placeholder="P"
        title="Profundidade (m)"
        aria-label="Profundidade (m)"
      />
    </span>
  );

  return (
    <div className={"movel" + (aberto ? " is-open" : "")}>
      <div className="movel__row">
        <span className="movel__code">{codigo}</span>
        <input
          className="cell-input movel__desc"
          value={v.descricao}
          onChange={set("descricao")}
          onBlur={salvar("descricao")}
          disabled={!editavel}
          placeholder="Descrição do móvel"
          aria-label="Móvel"
        />
        <input
          className="cell-input dim"
          value={v.puxador}
          onChange={set("puxador")}
          onBlur={salvar("puxador")}
          disabled={!editavel}
          placeholder="—"
          aria-label="Puxador"
        />
        <input
          className="cell-input dim"
          value={v.mecanismo}
          onChange={set("mecanismo")}
          onBlur={salvar("mecanismo")}
          disabled={!editavel}
          placeholder="—"
          aria-label="Mecanismo"
        />
        {/* Dimensoes EDITAVEIS na linha (mockup); CAD = read-only da composicao */}
        <span className="movel__dimcell">
          {cad ? (
            <span className="movel__dimview">
              <span className="mono">{dimsCad(item) || "—"}</span>
            </span>
          ) : (
            dimInputs
          )}
          <span className="u">m</span>
        </span>
        <input
          className="cell-input mono qnt"
          value={v.quantidade}
          onChange={set("quantidade")}
          onBlur={salvarQnt}
          disabled={!editavel}
          inputMode="decimal"
          aria-label="Quantidade"
        />
        <span className="movel__valor">
          {temLinhas || cad ? (
            // Duas linhas dentro da propria coluna: o preco ao cliente e o
            // numero principal (e o que soma no ambiente e no total); o custo
            // fica abaixo, discreto. Empilhar evita estourar os 112px da
            // coluna e invadir DIMENSOES/QNT (zero scroll lateral).
            <span className="movel__valor-calc" title={tituloValor}>
              <span className="movel__preco mono">
                {fmtMoeda(valorBloqueado)}{" "}
                <FiLock size={10} aria-hidden="true" />
              </span>
              {custoMovel !== null && (
                <span className="movel__custo mono">
                  custo {fmtMoeda(custoMovel)}
                </span>
              )}
            </span>
          ) : (
            <>
              <span className="cur">R$</span>
              <input
                className="cell-input mono"
                value={v.valor_unitario}
                onChange={set("valor_unitario")}
                onBlur={salvarValor}
                disabled={!editavel}
                inputMode="decimal"
                aria-label="Valor unitario"
              />
            </>
          )}
        </span>
        <span className="movel__rowactions">
          <button
            type="button"
            className="mini-btn chev"
            onClick={() => setAberto((x) => !x)}
            aria-label={aberto ? "Recolher detalhes" : "Expandir detalhes"}
          >
            <FiChevronDown size={14} aria-hidden="true" />
          </button>
        </span>
      </div>

      {aberto && (
        <div className="movel__detail">
          {cad ? (
            <div className="field span3">
              <ComposicaoCad item={item} />
            </div>
          ) : (
            <>
              <label className="field">
                Acabamento interno
                <input
                  className="input"
                  value={v.material}
                  onChange={set("material")}
                  onBlur={salvar("material")}
                  disabled={!editavel}
                  placeholder="Ex.: MDF Branco TX"
                />
              </label>
              <label className="field">
                Acabamento externo
                <input
                  className="input"
                  value={v.acabamento}
                  onChange={set("acabamento")}
                  onBlur={salvar("acabamento")}
                  disabled={!editavel}
                  placeholder="Ex.: MDF a definir"
                />
              </label>
              <div className="field">
                Dimensões — L × A × P (m)
                {dimInputs}
              </div>
            </>
          )}
          <label className="field span2">
            Observações do móvel
            <input
              className="input"
              value={v.observacao}
              onChange={set("observacao")}
              onBlur={salvar("observacao")}
              disabled={!editavel}
              placeholder="Ex.: tampo em quartzo não incluso, recorte para coifa..."
            />
          </label>
          {/* Motor DIVISOR: materiais e ferragens DENTRO do movel. Adicionar a
              primeira linha trava o preco livre (vira calculado); remover a
              ultima devolve o campo livre. */}
          {materiais && (
            <div className="field span3" style={{ display: "grid", gap: 12 }}>
              <AreasM2Editor
                ambienteId={ambienteId}
                itemId={item.id}
                linhas={materiais.areas || []}
                produtosChapa={produtosChapa}
                editavel={editavel}
                titulo="Materiais do móvel (m² e chapas)"
                onMutate={onMutateMateriais}
              />
              <InsumosEditor
                ambienteId={ambienteId}
                itemId={item.id}
                linhas={materiais.insumos || []}
                produtos={produtosInsumo}
                editavel={editavel}
                titulo="Ferragens do móvel"
                onMutate={onMutateMateriais}
              />
              {/* Terceiros/servicos DESTE movel (laqueacao, vidro, marmore de
                  um movel especifico). Cotacao criada aqui nasce com item +
                  ambiente e nao aparece no bloco do ambiente. */}
              {orcamento && (
                <CotacoesBloco
                  orcamento={orcamento}
                  ambientes={ambienteResumo ? [ambienteResumo] : []}
                  ambienteId={ambienteId}
                  itemId={item.id}
                  cotacoesExternas={cotacoes}
                  editavel={editavel}
                  divisor={divisorPreco}
                  onChange={onMutateCotacoes}
                />
              )}
            </div>
          )}
          {editavel && (
            <div className="field" style={{ justifyContent: "flex-end" }}>
              <button
                type="button"
                className="mini-btn danger"
                onClick={onDelete}
                style={{ alignSelf: "flex-end" }}
              >
                <FiTrash2 size={13} aria-hidden="true" /> Remover móvel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Card de ambiente
// ---------------------------------------------------------------

function AmbienteCard({
  orcamento,
  ambiente,
  indice,
  editavel,
  recolhido,
  onToggle,
  onChange,
  motorDivisor = false,
  produtosChapa = null,
  produtosInsumo = null,
  precoPorItem = null,
  subtotalPreco = null,
  divisorPreco = 0,
  // Cotacoes do ORCAMENTO inteiro (um GET so, feito pelo AmbientesV2). Cada
  // bloco filtra o que e seu; aqui separamos as de movel das do ambiente.
  cotacoes = [],
}) {
  const { request } = useAuth();
  const [nome, setNome] = useState(ambiente.nome || "");

  const itens = ambiente.itens || [];
  const cad = ambienteTemCad(ambiente);
  const ehM2 = ambienteEhM2(ambiente);
  const ehProjecaoTerceiro = ambienteEhProjecaoTerceiro(ambiente);
  const motorNoAmbiente = motorDivisor && !ehProjecaoTerceiro;

  // Subtotal: no motor DIVISOR soma os preco_projetado dos moveis + itens
  // manuais (payload /preco); fallback local enquanto o payload nao chegou.
  const subtotalLocal = itens.reduce(
    (acc, it) => acc + Number(it.quantidade || 0) * Number(it.valor_unitario || 0),
    0,
  );
  const subtotal = subtotalPreco ?? subtotalLocal;

  // ---- linhas do motor (AreaAmbiente + InsumoAmbiente) do ambiente ----
  // Um GET de cada por card; a distribuicao por movel e em memoria.
  const [linhasMotor, setLinhasMotor] = useState(null);

  const carregarLinhas = useCallback(async () => {
    if (!motorNoAmbiente) return;
    try {
      const [areasPayload, insumosPayload] = await Promise.all([
        listarAreasAmbiente(request, ambiente.id, { silentError: true }),
        listarInsumosAmbiente(request, ambiente.id, { silentError: true }),
      ]);
      setLinhasMotor({
        areas: normalizarLista(areasPayload),
        insumos: normalizarLista(insumosPayload),
      });
    } catch {
      setLinhasMotor({ areas: [], insumos: [] });
    }
  }, [request, ambiente.id, motorNoAmbiente]);

  useEffect(() => {
    carregarLinhas();
  }, [carregarLinhas]);

  const areasAmbiente = linhasMotor?.areas || [];
  const insumosAmbiente = linhasMotor?.insumos || [];
  const materiaisDoMovel = (itemId) => ({
    areas: areasAmbiente.filter((l) => String(l.item || "") === String(itemId)),
    insumos: insumosAmbiente.filter(
      (l) => String(l.item || "") === String(itemId),
    ),
  });
  // Linhas legadas sem movel (item null) — só leitura/remocao, sem criar.
  const areasLegado = areasAmbiente.filter((l) => !l.item);
  const insumosLegado = insumosAmbiente.filter((l) => !l.item);

  const cotacoesDoMovel = (itemId) =>
    (cotacoes || []).filter((c) => String(c.item || "") === String(itemId));
  // Resumo do ambiente (com os móveis) para o bloco de cotações resolver o
  // nome do escopo sem outro GET.
  const ambienteResumo = { id: ambiente.id, nome: ambiente.nome, itens };

  const aposMutacaoMateriais = async () => {
    invalidarPreco();
    await carregarLinhas();
    if (onChange) await onChange();
  };

  const salvarNome = async () => {
    const limpo = nome.trim();
    if (!limpo || limpo === ambiente.nome) return;
    await atualizarAmbiente(request, ambiente.id, { nome: limpo });
    onChange();
  };

  // V17: o usuário precisa SABER se salvou. Save-on-blur é silencioso por
  // natureza — sem sinal, quem edita e sai da tela não tem como distinguir
  // "gravou" de "a request morreu no caminho".
  const [estadoSave, setEstadoSave] = useState(null); // 'salvando' | 'salvo' | 'erro'
  const timerSaveRef = useRef(null);
  useEffect(() => () => clearTimeout(timerSaveRef.current), []);

  // V15/V17: manda SÓ o campo que mudou.
  //
  // Antes reenviava 13 campos lidos do prop `item`, que é a foto do último
  // carregamento. Como cada blur dispara um save e o recarregamento é
  // assíncrono, editar dois campos em sequência fazia o segundo save
  // ressuscitar o valor antigo do primeiro — o dado sumia sem erro nenhum
  // na tela. É PATCH de verdade no backend (services/orcamentos.js:288),
  // então o corpo parcial basta.
  const patchItem = (item) => async (campos) => {
    clearTimeout(timerSaveRef.current);
    setEstadoSave("salvando");
    try {
      await atualizarItem(request, item.id, campos);
    } catch (err) {
      // O erro NÃO pode morrer aqui: o `request` do AuthContext já mostra o
      // toast, mas quem saiu da tela não vê toast nenhum. O selo fica
      // vermelho até a próxima gravação dar certo.
      setEstadoSave("erro");
      throw err;
    }
    if (motorDivisor) invalidarPreco();
    onChange();
    setEstadoSave("salvo");
    timerSaveRef.current = setTimeout(() => setEstadoSave(null), 2500);
  };

  const adicionarMovel = async () => {
    await criarItem(request, {
      ambiente: ambiente.id,
      descricao: "Novo móvel",
      quantidade: "1",
      unidade_medida: "UN",
      valor_unitario: "0.00",
      ordem: itens.length,
    });
    if (motorDivisor) invalidarPreco();
    onChange();
  };

  const removerItem = (item) => async () => {
    if (!window.confirm(`Remover "${item.descricao}"?`)) return;
    await deletarItem(request, item.id);
    if (motorDivisor) {
      invalidarPreco();
      await carregarLinhas();
    }
    onChange();
  };

  const duplicarAmbiente = async () => {
    const novo = await criarAmbienteNoOrcamento(request, ambiente.orcamento_id, {
      nome: `${ambiente.nome} (copia)`,
    });
    const novoId = novo?.id || novo?.data?.id;
    if (novoId) {
      for (const it of itens) {
        // Itens CAD nao sao duplicaveis (snapshot do import); copia so manuais.
        if (isCad(it)) continue;
        // eslint-disable-next-line no-await-in-loop
        await criarItem(request, {
          ambiente: novoId,
          descricao: it.descricao,
          quantidade: it.quantidade,
          unidade_medida: it.unidade_medida,
          valor_unitario: it.valor_unitario,
          ordem: it.ordem,
          largura_mm: it.largura_mm,
          altura_mm: it.altura_mm,
          profundidade_mm: it.profundidade_mm,
          material: it.material ?? "",
          acabamento: it.acabamento ?? "",
          observacao: it.observacao ?? "",
          puxador: it.puxador ?? "",
          mecanismo: it.mecanismo ?? "",
        });
      }
    }
    if (motorDivisor) invalidarPreco();
    onChange();
  };

  const excluirAmbiente = async () => {
    if (!window.confirm(`Excluir o ambiente "${ambiente.nome}" e todos os móveis?`)) return;
    await deletarAmbiente(request, ambiente.id);
    if (motorDivisor) invalidarPreco();
    onChange();
  };

  return (
    <section className={"amb" + (recolhido ? " is-collapsed" : "")}>
      <header className="amb__head">
        <button
          type="button"
          className="amb__chev"
          onClick={onToggle}
          aria-label={recolhido ? "Expandir ambiente" : "Recolher ambiente"}
        >
          <FiChevronDown size={15} aria-hidden="true" />
        </button>
        <div className="amb__id">
          <span className="amb__tag">
            {ehProjecaoTerceiro ? "Terceiros" : ehM2 ? "m²" : cad ? "3D" : "Ambiente"}
          </span>
          <input
            className="amb__name"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            onBlur={salvarNome}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            disabled={!editavel}
            aria-label={`Nome do ambiente ${indice + 1}`}
          />
        </div>
        <div className="meta">
          {estadoSave && (
            <span
              className="amb__save"
              data-estado={estadoSave}
              role="status"
              aria-live="polite"
            >
              {estadoSave === "salvando" && "Salvando…"}
              {estadoSave === "salvo" && "Salvo"}
              {estadoSave === "erro" && "Não salvou — tente de novo"}
            </span>
          )}
          <span className="amb__count">
            {itens.length} {itens.length === 1 ? "móvel" : "móveis"}
          </span>
          <span className="amb__subtotal">
            <span className="k">Subtotal</span> {fmtMoeda(subtotal)}
          </span>
        </div>
        {editavel && (
          <div className="amb__actions">
            <button
              type="button"
              className="mini-btn"
              onClick={duplicarAmbiente}
              title="Duplicar ambiente (moveis manuais)"
              aria-label="Duplicar ambiente"
            >
              <FiCopy size={13} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="mini-btn danger"
              onClick={excluirAmbiente}
              title="Excluir ambiente"
              aria-label="Excluir ambiente"
            >
              <FiTrash2 size={13} aria-hidden="true" />
            </button>
          </div>
        )}
      </header>

      {!recolhido && (
        <div className="amb__body">
          {itens.length > 0 && (
            <div className="movel-colhead" aria-hidden="true">
              <span>Cod.</span>
              <span>Móvel</span>
              <span>Puxador</span>
              <span>Mecanismo</span>
              <span>Dimensões (L×A×P)</span>
              <span>Qnt</span>
              <span>Valor</span>
              <span />
            </div>
          )}
          {itens.length === 0 && (
            <div className="amb-empty">Nenhum móvel neste ambiente ainda.</div>
          )}
          {itens.map((item, i) => (
            <MovelRow
              key={item.id}
              item={item}
              codigo={`M${String(i + 1).padStart(2, "0")}`}
              editavel={editavel}
              onPatch={patchItem(item)}
              onDelete={removerItem(item)}
              ambienteId={ambiente.id}
              materiais={motorNoAmbiente ? materiaisDoMovel(item.id) : null}
              produtosChapa={produtosChapa || []}
              produtosInsumo={produtosInsumo || []}
              precoMovel={precoPorItem ? precoPorItem.get(String(item.id)) : null}
              onMutateMateriais={aposMutacaoMateriais}
              orcamento={motorNoAmbiente ? orcamento : null}
              ambienteResumo={ambienteResumo}
              cotacoes={cotacoesDoMovel(item.id)}
              divisorPreco={divisorPreco}
              onMutateCotacoes={onChange}
            />
          ))}
          {editavel && (
            <button type="button" className="add-movel" onClick={adicionarMovel}>
              <FiPlus size={14} aria-hidden="true" /> Adicionar móvel
            </button>
          )}

          {/* Linhas legadas sem movel (item null): aparecem num bloco discreto
              apenas quando existem — sem criar novas aqui (materiais novos
              nascem DENTRO do movel). */}
          {motorNoAmbiente
            && (areasLegado.length > 0 || insumosLegado.length > 0) && (
            <div style={{ display: "grid", gap: 12, marginTop: 14, opacity: 0.9 }}>
              <div
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: "var(--text-secondary)",
                }}
              >
                Lançamentos do ambiente (legado — sem móvel)
              </div>
              <AreasM2Editor
                ambienteId={ambiente.id}
                itemId={null}
                linhas={areasLegado}
                produtosChapa={produtosChapa || []}
                editavel={editavel}
                permitirCriar={false}
                titulo={null}
                onMutate={aposMutacaoMateriais}
              />
              <InsumosEditor
                ambienteId={ambiente.id}
                itemId={null}
                linhas={insumosLegado}
                produtos={produtosInsumo || []}
                editavel={editavel}
                permitirCriar={false}
                titulo={null}
                onMutate={aposMutacaoMateriais}
              />
            </div>
          )}

          {/* Terceiros e servicos do AMBIENTE (os de um movel especifico ficam
              dentro do card do movel — este bloco os exclui). Cotacao criada
              aqui ja nasce com o ambiente pre-selecionado. */}
          {motorNoAmbiente && orcamento && (
            <div style={{ marginTop: 14 }}>
              <CotacoesBloco
                orcamento={orcamento}
                ambientes={[ambienteResumo]}
                ambienteId={ambiente.id}
                cotacoesExternas={cotacoes}
                editavel={editavel}
                divisor={divisorPreco}
                onChange={onChange}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------
// Secao completa (toolbar + cards)
// ---------------------------------------------------------------

function AmbientesV2({ orcamento, editavel, onChange, onAbrirImport3D }) {
  const { request } = useAuth();
  const [recolhidos, setRecolhidos] = useState(() => new Set());

  // Motor DIVISOR: catalogo de produtos carregado UMA vez aqui e distribuido
  // aos editores de todos os ambientes (evita 2 GETs de catalogo por card).
  const motorDivisor = orcamento?.motor_preco === "DIVISOR";
  const [catalogoProdutos, setCatalogoProdutos] = useState(null);

  useEffect(() => {
    if (!motorDivisor) return undefined;
    let ativo = true;
    (async () => {
      try {
        const payload = await listarProdutos(request, {
          ativo: true,
          pageSize: 500,
        });
        const lista = Array.isArray(payload) ? payload : payload?.results || [];
        if (ativo) setCatalogoProdutos(lista);
      } catch {
        if (ativo) setCatalogoProdutos([]);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [request, motorDivisor]);

  const produtosChapa = useMemo(
    () => (catalogoProdutos || []).filter((p) => p.categoria === "CHAPA"),
    [catalogoProdutos],
  );
  // Chapas e modulos nao sao insumos (mesma regra do InsumosEditor).
  const produtosInsumo = useMemo(
    () => (catalogoProdutos || []).filter(
      (p) => p.categoria !== "CHAPA" && p.categoria !== "MODULO",
    ),
    [catalogoProdutos],
  );

  // Payload de GET /preco: campo "moveis" (preco_projetado por movel) +
  // "adicionais" (itens manuais). Alimenta o valor bloqueado de cada movel e
  // o subtotal do card do ambiente. Recarregado silenciosamente no mesmo
  // evento que o ComposicaoPrecoDivisor escuta.
  const [precoPayload, setPrecoPayload] = useState(null);

  const carregarPreco = useCallback(async () => {
    if (!motorDivisor || !orcamento?.id) return;
    try {
      const payload = await getPrecoOrcamento(request, orcamento.id, {
        silentError: true,
      });
      setPrecoPayload(payload);
    } catch {
      setPrecoPayload(null);
    }
  }, [request, orcamento?.id, motorDivisor]);

  // Cotacoes de terceiro do orcamento inteiro: UM GET para a tela toda. Elas
  // aparecem em dois lugares (bloco do ambiente e bloco de cada movel) e ainda
  // decidem se o preco do movel esta travado — por isso vivem aqui, e nao
  // dentro de cada CotacoesBloco.
  const [cotacoes, setCotacoes] = useState([]);

  const carregarCotacoes = useCallback(async () => {
    if (!motorDivisor || !orcamento?.id) return;
    try {
      const payload = await listarCotacoes(
        request,
        { orcamento: orcamento.id },
        { silentError: true },
      );
      setCotacoes(normalizarLista(payload));
    } catch {
      setCotacoes([]);
    }
  }, [request, orcamento?.id, motorDivisor]);

  useEffect(() => {
    carregarPreco();
    carregarCotacoes();
    // updated_at cobre mutacoes que recarregam o orcamento sem disparar o
    // evento (ex.: transicoes de situacao feitas em outro bloco da tela).
  }, [carregarPreco, carregarCotacoes, orcamento?.updated_at]);

  useEffect(() => {
    if (!motorDivisor) return undefined;
    const handler = () => {
      carregarPreco();
      carregarCotacoes();
    };
    window.addEventListener("orc2:preco-invalidado", handler);
    return () => window.removeEventListener("orc2:preco-invalidado", handler);
  }, [carregarPreco, carregarCotacoes, motorDivisor]);

  const precoPorItem = useMemo(() => {
    const mapa = new Map();
    (precoPayload?.moveis || []).forEach((m) => {
      mapa.set(String(m.item_id), m);
    });
    return mapa;
  }, [precoPayload]);

  const subtotalPorAmbiente = useMemo(() => {
    // Sem payload (ou payload sem os campos novos): fallback local no card.
    if (!precoPayload || (!precoPayload.moveis && !precoPayload.adicionais)) {
      return null;
    }
    const mapa = new Map();
    const somar = (ambId, valor) => {
      if (!Number.isFinite(valor)) return;
      const chave = String(ambId);
      mapa.set(chave, (mapa.get(chave) || 0) + valor);
    };
    (precoPayload.moveis || []).forEach((m) => {
      somar(m.ambiente_id, Number(m.preco_alocado ?? m.preco_projetado ?? 0));
    });
    (precoPayload.adicionais || []).forEach((a) => {
      somar(a.ambiente_id, Number(a.valor ?? 0));
    });
    return mapa;
  }, [precoPayload]);

  // 0 = "sem divisor ainda" para o preview do CotacoesBloco (evita o GET
  // /preco proprio de cada card enquanto o payload nao chegou).
  const divisorPreco = precoPayload ? Number(precoPayload.divisor) || 0 : 0;

  const ambientes = useMemo(() => {
    const lista = orcamento?.versao_ativa?.ambientes || [];
    // orcamento_id usado pelo duplicar (criarAmbienteNoOrcamento).
    return lista.map((a) => ({ ...a, orcamento_id: orcamento.id }));
  }, [orcamento]);

  const toggle = (id) => () =>
    setRecolhidos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Botao UNICO Recolher/Expandir (pedido da usuaria 2026-06-10):
  // alterna entre recolher todos e expandir todos conforme o estado.
  const todosRecolhidos =
    ambientes.length > 0 && ambientes.every((a) => recolhidos.has(a.id));

  const adicionarAmbiente = async () => {
    await criarAmbienteNoOrcamento(request, orcamento.id, {
      nome: `Novo ambiente`,
    });
    onChange();
  };

  // C10 (auditoria 2026-08-07, portado do EditorAmbientes v1 aposentado):
  // 3 banners de precificacao CAD (ADR-0019 D9) + botao "Atualizar precos
  // do estoque" chamando POST /recalcular-precos. Sem isso o vendedor via
  // "Enviar (bloqueado)" no header e nao tinha NENHUM caminho na tela v2
  // para destravar o orcamento depois que o estoque cadastrava o produto.
  const statusPrecificacao = orcamento?.precificacao_status || {
    pendentes_estoque: 0,
    zerados_apos_cadastro: 0,
    total_bloqueando: 0,
  };
  const desatualizados = Number(orcamento?.items_desatualizados_count || 0);

  const recalcularPrecos = async () => {
    await recalcularPrecosOrcamento(request, orcamento.id);
    if (motorDivisor) invalidarPreco();
    if (onChange) await onChange();
  };

  return (
    <>
      {/* Banner DANGER — items aguardando precificacao pelo estoque (D9) */}
      {statusPrecificacao.pendentes_estoque > 0 && (
        <div
          role="alert"
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            background: "rgba(220,38,38,0.10)",
            border: "1px solid rgba(220,38,38,0.35)",
            color: "rgb(140,30,30)",
            fontSize: "0.88rem",
            lineHeight: 1.45,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <FiLock size={16} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <strong>
              {statusPrecificacao.pendentes_estoque} item(s) aguardando
              precificacao pelo estoque.
            </strong>
            {" "}O estoque ja foi notificado por email. Voce nao podera enviar
            este orcamento ao cliente ate o cadastro ficar completo.
          </div>
          <a
            href="/estoque/pendentes"
            target="_blank"
            rel="noreferrer"
            style={{ color: "rgb(140,30,30)", fontWeight: 600 }}
          >
            Acompanhar fila →
          </a>
        </div>
      )}

      {/* Banner WARNING — estoque cadastrou mas vendedor nao recalculou (D9) */}
      {statusPrecificacao.zerados_apos_cadastro > 0 && (
        <div
          role="alert"
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            background: "rgba(234,140,30,0.12)",
            border: "1px solid rgba(234,140,30,0.4)",
            color: "rgb(140,80,0)",
            fontSize: "0.88rem",
            lineHeight: 1.45,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            ⚠ Estoque ja cadastrou {statusPrecificacao.zerados_apos_cadastro}{" "}
            item(s) que estao com preco zerado. Atualize antes de enviar.
          </div>
          {editavel && (
            <AsyncButton
              variant="secondary"
              onClick={recalcularPrecos}
              successMessage="Precos atualizados"
              silentError
            >
              Atualizar precos do estoque
            </AsyncButton>
          )}
        </div>
      )}

      {/* Banner INFO — items com preco antigo (estoque mudou custo) */}
      {desatualizados > 0 && statusPrecificacao.zerados_apos_cadastro === 0 && (
        <div
          role="status"
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            background: "rgba(30,120,200,0.10)",
            border: "1px solid rgba(30,120,200,0.30)",
            color: "rgb(20,80,140)",
            fontSize: "0.88rem",
            lineHeight: 1.45,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            {desatualizados} item(s) com preco antigo (estoque atualizou
            apos o import).
          </div>
          {editavel && (
            <AsyncButton
              variant="secondary"
              onClick={recalcularPrecos}
              successMessage="Precos atualizados"
              silentError
            >
              Atualizar precos
            </AsyncButton>
          )}
        </div>
      )}

      <div className="amb-toolbar">
        <div className="lhs">
          <h2>Ambientes</h2>
          <span className="ct">{ambientes.length}</span>
        </div>
        <div className="rhs">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() =>
              setRecolhidos(
                todosRecolhidos ? new Set() : new Set(ambientes.map((a) => a.id)),
              )
            }
            disabled={ambientes.length === 0}
          >
            <FiChevronDown
              size={13}
              aria-hidden="true"
              style={todosRecolhidos ? undefined : { transform: "rotate(180deg)" }}
            />{" "}
            {todosRecolhidos ? "Expandir" : "Recolher"}
          </button>
          {editavel && (
            <>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={onAbrirImport3D}
              >
                <FiUploadCloud size={14} aria-hidden="true" /> Importar 3D
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={adicionarAmbiente}
              >
                <FiPlus size={14} aria-hidden="true" /> Adicionar ambiente
              </button>
            </>
          )}
        </div>
      </div>

      {ambientes.length === 0 && (
        <div className="amb-empty" style={{ marginBottom: 12 }}>
          Nenhum ambiente. Adicione manualmente ou importe do 3D.
        </div>
      )}

      {ambientes.map((amb, i) => (
        <AmbienteCard
          key={amb.id}
          orcamento={orcamento}
          ambiente={amb}
          indice={i}
          editavel={editavel}
          recolhido={recolhidos.has(amb.id)}
          onToggle={toggle(amb.id)}
          onChange={onChange}
          motorDivisor={motorDivisor}
          produtosChapa={produtosChapa}
          produtosInsumo={produtosInsumo}
          precoPorItem={motorDivisor ? precoPorItem : null}
          subtotalPreco={
            subtotalPorAmbiente
              ? subtotalPorAmbiente.get(String(amb.id)) ?? 0
              : null
          }
          divisorPreco={divisorPreco}
          cotacoes={cotacoes}
        />
      ))}
    </>
  );
}

export default AmbientesV2;
